import React from 'react';
import { useParams } from 'react-router-dom';
import { singleBlogPostQuery } from '../lib/queries';
import { useSanityPreview } from '../lib/useSanityPreview';
import { PortableText } from '@portabletext/react';
import CreatorProfile from './CreatorProfile';

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  publishedDate: string;
  author: string;
  authorImageUrl?: string;
  content: any[]; // Full content for single post view
  featuredImageUrl: string;
  postType: string;
  tags: string[];
}

const SingleBlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  
  // Only fetch the full blog post content when viewing a specific post
  const { data: post, error, isLoading } = useSanityPreview<BlogPost>(
    singleBlogPostQuery(slug || ''),
    {}
  );

  if (isLoading) {
    return <div className="w-full h-screen flex items-center justify-center">Loading...</div>;
  }

  if (error) {
    return <div className="w-full h-screen flex items-center justify-center">Error: {error.message}</div>;
  }

  if (!post) {
    return <div className="w-full h-screen flex items-center justify-center">Blog post not found</div>;
  }

  return (
    <div className="min-h-screen bg-white pt-24">
      <div className="max-w-[2000px] mx-auto px-6">
        <article className="mb-32">
          {post.featuredImageUrl && (
            <div className="aspect-[21/9] mb-8">
              <img
                src={post.featuredImageUrl}
                alt={post.title || ''}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-ogg font-light mb-4 blog-post-title">{post.title}</h2>
            <div className="mb-8 text-gray-600">
              {post.publishedDate && (
                <span>{new Date(post.publishedDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              )}
              {post.author && (
                <>
                  <span className="mx-2">·</span>
                  <CreatorProfile 
                    name={post.author}
                    imageUrl={post.authorImageUrl}
                    size="sm"
                    showName={true}
                  />
                </>
              )}
              {post.tags?.length > 0 && (
                <>
                  <span className="mx-2">·</span>
                  <span>{post.tags.join(', ')}</span>
                </>
              )}
            </div>
            {post.content && (
              <div className="prose prose-lg max-w-none blog-content">
                <PortableText value={post.content} />
              </div>
            )}
            <div className="mt-12">
              <a 
                href="/journal" 
                className="inline-block px-6 py-2 border border-black hover:bg-black hover:text-white transition-colors duration-300"
              >
                Back to Journal
              </a>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default SingleBlogPost;
